/*
 * TSS Skin Quiz — Analytics + Data Capture Layer (MVP-2)
 * ------------------------------------------------------------
 * Additive module. MVP-1 works without this. Installing this enables:
 *   1. Row-per-session writes to Supabase (answers, UTMs, persona, cart)
 *   2. GA4 events at each funnel step
 *   3. UTM + referrer + device capture on quiz start
 *   4. Add-to-cart tracking
 *
 * BEFORE LOADING THIS FILE in Shopify, you MUST:
 *   1. Create a Supabase project (free — 2 min, see MVP-2-INSTALL.md)
 *   2. Run the schema SQL in Supabase SQL editor
 *   3. Replace SUPABASE_URL + SUPABASE_ANON_KEY below with your project's values
 *
 * This file is safe to omit if you don't want data capture yet.
 */
(function () {
  'use strict';

  // =========================================================================
  // CONFIG — replace these with your Supabase project's values
  // =========================================================================
  var SUPABASE_URL      = 'https://oqixpqnyzyiczcsxawap.supabase.co';
  var SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9xaXhwcW55enlpY3pjc3hhd2FwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcwMTQ1MjgsImV4cCI6MjA5MjU5MDUyOH0.VkULoeOaAXITNZw_VH16JLwNFgPPbsNdZAKyUi5ylW8';
  var SUPABASE_TABLE    = 'quiz_sessions';

  // GA4 custom event names (keep consistent for reporting)
  var GA4_EVENTS = {
    start: 'tss_quiz_start',
    q1: 'tss_quiz_q1', q2: 'tss_quiz_q2', q2b: 'tss_quiz_q2b',
    q3: 'tss_quiz_q3', q4: 'tss_quiz_q4', q5: 'tss_quiz_q5',
    q6: 'tss_quiz_q6', q7: 'tss_quiz_q7', contact: 'tss_quiz_contact',
    complete: 'tss_quiz_complete',
    pdp_view: 'tss_quiz_pdp_view',
    // ATC event names. The DOM CustomEvents fired by pdp.js are
    // 'add_to_cart_single' and 'add_to_cart_full_routine'. Keeping
    // the GA4 names short ('add_single' / 'add_all') for cleaner reports.
    add_single: 'tss_quiz_add_single',
    add_all: 'tss_quiz_add_all'
  };
  // =========================================================================

  var STORAGE_KEY = 'tss_quiz_session_v1';

  // ---- UTM + context capture ----------------------------------------------
  function captureContext() {
    var params = new URLSearchParams(window.location.search);
    var ctx = {
      utm_source:   params.get('utm_source')   || getCookie('tss_utm_source')   || null,
      utm_medium:   params.get('utm_medium')   || getCookie('tss_utm_medium')   || null,
      utm_campaign: params.get('utm_campaign') || getCookie('tss_utm_campaign') || null,
      utm_content:  params.get('utm_content')  || getCookie('tss_utm_content')  || null,
      utm_term:     params.get('utm_term')     || getCookie('tss_utm_term')     || null,
      referrer:     document.referrer || null,
      entry_url:    window.location.href,
      device_type:  /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
      user_agent:   navigator.userAgent
    };
    // Persist UTMs in cookies for 7 days so they survive session
    ['utm_source','utm_medium','utm_campaign','utm_content','utm_term'].forEach(function (k) {
      if (params.get(k)) setCookie('tss_' + k, params.get(k), 7);
    });
    return ctx;
  }

  function setCookie(name, value, days) {
    var d = new Date(); d.setTime(d.getTime() + days * 864e5);
    document.cookie = name + '=' + encodeURIComponent(value) + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  }
  function getCookie(name) {
    var c = document.cookie.split(';').map(function(x){ return x.trim(); }).find(function(x){ return x.indexOf(name + '=') === 0; });
    return c ? decodeURIComponent(c.split('=')[1]) : null;
  }

  // ---- Supabase REST writer -----------------------------------------------
  function supabaseUpsert(payload) {
    if (!SUPABASE_URL || SUPABASE_URL.indexOf('YOUR_PROJECT_REF') !== -1) {
      console.warn('[TSS Quiz Analytics] Supabase not configured — skipping DB write.');
      return Promise.resolve();
    }
    return fetch(SUPABASE_URL + '/rest/v1/' + SUPABASE_TABLE, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': 'Bearer ' + SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'resolution=merge-duplicates,return=minimal'
      },
      body: JSON.stringify(payload),
      keepalive: true // survives page unload
    }).catch(function (err) {
      console.warn('[TSS Quiz Analytics] Supabase write failed:', err);
    });
  }

  function getSessionState() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch (e) { return null; }
  }

  // Builds a full row payload for upsert. session_id is PK → upsert updates existing row.
  function buildRow(extra) {
    var s = getSessionState();
    if (!s) return null;
    var ctx = s.context || captureContext();
    var a = s.answers || {};
    var row = {
      session_id:    s.session_id,
      started_at:    s.started_at,
      updated_at:    new Date().toISOString(),
      entry_source:  s.entry_source,
      completed:     !!s.completed,
      persona_id:    s.persona && s.persona.persona_id || null,
      persona_json:  s.persona || null,
      answers_json:  a,
      skin_type:     a.skin_type,
      concerns:      a.concerns || [],
      acne_freq:     a.acne_freq,
      makeup:        a.makeup,
      free_text:     a.free_text || '',
      sensitivity:   a.sensitivity,
      tried_before:  a.tried_before || [],
      phone:         a.phone || null,
      email:         a.email || null,
      utm_source:    ctx.utm_source,
      utm_medium:    ctx.utm_medium,
      utm_campaign:  ctx.utm_campaign,
      utm_content:   ctx.utm_content,
      utm_term:      ctx.utm_term,
      referrer:      ctx.referrer,
      entry_url:     ctx.entry_url,
      device_type:   ctx.device_type
    };
    if (extra) Object.keys(extra).forEach(function (k) { row[k] = extra[k]; });
    return row;
  }

  function push(extra) {
    var row = buildRow(extra);
    if (row) supabaseUpsert(row);
  }

  // ---- GA4 event emitter ---------------------------------------------------
  function ga4(name, params) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params || {});
    } else if (window.dataLayer && window.dataLayer.push) {
      window.dataLayer.push(Object.assign({ event: name }, params || {}));
    }
  }

  // ---- CustomerLabs dataLayer push ----------------------------------------
  // CL reads window.dataLayer for its event pipeline and Meta CAPI forwarding.
  // We push alongside (not instead of) GA4 so both platforms get clean,
  // structured event records. Pattern matches CL's documented payload shape:
  //
  //   dataLayer.push({
  //     event: 'tss_quiz_complete',
  //     user_data: { email, phone, external_id, ... },
  //     <namespace>_data: { ...event-specific fields }
  //   })
  //
  // Failure is silently swallowed, this layer must NEVER break the quiz flow.
  function clabsUserData(state, override) {
    var s = state || getSessionState() || {};
    var a = s.answers || {};
    var ctx = s.context || {};
    var data = {};
    if (s.session_id)              data.external_id     = s.session_id;
    if (a.email)                   data.email           = a.email;
    if (a.phone)                   data.phone           = a.phone;
    if (a.skin_type)               data.skin_type       = a.skin_type;
    if (s.persona && s.persona.persona_id) data.persona_id = s.persona.persona_id;
    // Optional channel context, CL uses this for attribution stitching
    if (ctx.utm_source)            data.utm_source      = ctx.utm_source;
    if (ctx.utm_medium)            data.utm_medium      = ctx.utm_medium;
    if (ctx.utm_campaign)          data.utm_campaign    = ctx.utm_campaign;
    if (ctx.device_type)           data.device_type     = ctx.device_type;
    if (override) Object.keys(override).forEach(function (k) { data[k] = override[k]; });
    return data;
  }

  function clabsPush(eventName, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      var s = getSessionState() || {};
      var entry = Object.assign(
        { event: eventName, user_data: clabsUserData(s) },
        payload || {}
      );
      window.dataLayer.push(entry);
    } catch (err) {
      // CL push must never throw, swallow and keep going
      console.warn('[TSS Quiz Analytics] CL push failed:', err);
    }
  }

  // ---- Event listeners -----------------------------------------------------
  document.addEventListener('tss-quiz:start', function (e) {
    // Attach captured context to session state for downstream writes
    var s = getSessionState();
    if (s && !s.context) {
      s.context = captureContext();
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch (err) {}
    }
    push({ event_name: 'start' });
    ga4(GA4_EVENTS.start, { source: e.detail.source, session_id: e.detail.session_id });
    clabsPush('cl_quiz_start', {
      quiz_data: {
        source:     e.detail.source || null,
        session_id: e.detail.session_id
      }
    });
  });

  document.addEventListener('tss-quiz:answer', function (e) {
    var q = e.detail.q;
    push({ event_name: q });
    var gaName = GA4_EVENTS[q] || ('tss_quiz_' + q);
    ga4(gaName, {
      value: typeof e.detail.value === 'object' ? JSON.stringify(e.detail.value) : e.detail.value,
      skipped: !!e.detail.skipped
    });
    clabsPush('cl_quiz_answer', {
      quiz_data: {
        question:   q,
        value:      e.detail.value,
        skipped:    !!e.detail.skipped,
        session_id: e.detail.session_id || (getSessionState() || {}).session_id
      }
    });
  });

  document.addEventListener('tss-quiz:step_view', function (e) {
    push({ last_step_seen: e.detail.step });
    ga4('tss_quiz_step_view', { step: e.detail.step, session_id: e.detail.session_id });
    clabsPush('cl_quiz_step_view', {
      quiz_data: {
        step:       e.detail.step,
        session_id: e.detail.session_id
      }
    });
  });

  // ---- Photo step events (v2) ---------------------------------------------
  // These all fan out the same way, so they are registered in a loop rather
  // than as a dozen near-identical listeners.
  //
  // PRIVACY: no image data is ever put in any of these payloads. The photo
  // itself never leaves the device except to the vision Worker, and the
  // thumbnail never leaves the device at all. Only the derived fields below
  // are recorded.
  //
  // The 'photo' and 'reconcile' ANSWER events are not here. They already flow
  // through the generic tss-quiz:answer listener above.
  var PHOTO_EVENTS = [
    'photo_opt_in', 'photo_skip', 'photo_consent_given', 'photo_captured',
    'photo_validation_fail', 'photo_retake', 'photo_analysis_success',
    'photo_analysis_fail', 'photo_partial_read', 'findings_applied',
    'findings_reconciled', 'findings_shown', 'photo_reoffer_shown',
    'photo_reoffer_taken', 'findings_applied_reoffer'
  ];

  // Events emitted from tss-quiz-pdp.js go through its own track(), which
  // ALREADY calls gtag('event', 'tss_quiz_' + name). Calling ga4() for them
  // here as well would send every one to GA4 twice. Same class of bug as the
  // v5 CustomerLabs rename, so these get Supabase and CL only.
  var PDP_EMITTED = [
    'findings_shown', 'photo_reoffer_shown', 'photo_reoffer_taken',
    'findings_applied_reoffer'
  ];

  // Only some of them are worth a column on the session row. The rest go to
  // GA4 and CustomerLabs only. Returning null means "do not touch Supabase".
  function photoRow(name, d) {
    if (name === 'photo_opt_in')  return { photo_opted_in: true };
    if (name === 'photo_skip')    return { photo_opted_in: false };
    if (name === 'photo_captured') return { photo_attempts: d.attempt || 1 };
    if (name === 'photo_analysis_success') {
      var st = getSessionState() || {};
      return {
        photo_usable: true,
        photo_findings: ((st.answers || {}).photo_read) || null
      };
    }
    if (name === 'photo_partial_read') {
      return { photo_usable: false, photo_attempts: d.attempts || null };
    }
    if (name === 'findings_applied' || name === 'findings_applied_reoffer') {
      return {
        vision_added:   d.added || '',
        vision_removed: d.removed || ''
      };
    }
    if (name === 'findings_reconciled') return { vision_reconcile: d.value || '' };
    return null;
  }

  PHOTO_EVENTS.forEach(function (name) {
    document.addEventListener('tss-quiz:' + name, function (e) {
      var d = e.detail || {};
      try {
        var row = photoRow(name, d);
        if (row) push(row);
      } catch (err) {}
      if (PDP_EMITTED.indexOf(name) === -1) ga4('tss_quiz_' + name, d);
      clabsPush('cl_quiz_' + name, { quiz_data: d });
    });
  });

  document.addEventListener('tss-quiz:complete', function (e) {
    var products = (e.detail.persona && e.detail.persona.products) || [];
    push({ event_name: 'complete', completed: true, suggested_products: products });
    if (typeof window.gtag === 'function') {
      window.gtag('set', 'user_properties', { quiz_taker: 'true' });
    } else if (window.dataLayer) {
      window.dataLayer.push({ quiz_taker: 'true' });
    }
    ga4(GA4_EVENTS.complete, {
      persona_id: e.detail.persona && e.detail.persona.persona_id,
      session_id: e.detail.session_id,
      active_count: e.detail.persona && e.detail.persona.active_count
    });
    // The complete event is the richest payload, the user has now provided
    // identity (phone always, email optional) and we have the full persona.
    // Push everything CL might want to analyse later.
    var s = getSessionState() || {};
    var a = s.answers || {};
    var p = s.persona || e.detail.persona || {};
    clabsPush('cl_quiz_complete', {
      quiz_data: {
        session_id:         e.detail.session_id || s.session_id,
        persona_id:         p.persona_id || null,
        active_count:       p.active_count || 0,
        suggested_products: products,
        skin_type:          a.skin_type || null,
        concerns:           a.concerns || [],
        concern_buckets:    p.buckets ? Object.keys(p.buckets) : [],
        acne_freq:          a.acne_freq || null,
        triggers:           a.triggers || [],
        sensitivity:        a.sensitivity || null,
        free_text:          a.free_text || '',
        barrier_first:      !!p.barrier_first,
        am_routine:         p.am || [],
        pm_routine:         p.pm || []
      }
    });
  });

  document.addEventListener('tss-quiz:pdp_view', function (e) {
    push({ event_name: 'pdp_view', pdp_viewed: true });
    ga4(GA4_EVENTS.pdp_view, {
      persona_id: e.detail.persona && e.detail.persona.persona_id
    });
    clabsPush('cl_quiz_pdp_view', {
      quiz_data: {
        session_id: e.detail.session_id || (getSessionState() || {}).session_id,
        persona_id: e.detail.persona_id || (e.detail.persona && e.detail.persona.persona_id) || null,
        ai_source:  e.detail.ai_source || null
      }
    });
  });

  // pdp.js fires 'tss-quiz:add_to_cart_single' on individual ATC and
  // 'tss-quiz:add_to_cart_full_routine' on bundle ATC. We listen on those
  // exact names, the previous shorter names ('add_single' / 'add_all') were
  // never being fired. Keeping the shorter names for the GA4 event so the
  // reports stay clean, but the DOM listener has to match what's emitted.
  document.addEventListener('tss-quiz:add_to_cart_single', function (e) {
    push({
      event_name: 'add_single',
      cart_added: true,
      cart_items: [{ code: e.detail.code, variant_id: e.detail.variant_id }]
    });
    ga4(GA4_EVENTS.add_single, {
      code: e.detail.code,
      variant_id: e.detail.variant_id,
      session_id: e.detail.session_id,
      persona_id: e.detail.persona_id
    });
    clabsPush('cl_quiz_add_to_cart_single', {
      quiz_data: {
        session_id: e.detail.session_id,
        persona_id: e.detail.persona_id,
        product_code: e.detail.code || null,
        variant_id: e.detail.variant_id || null
      }
    });
  });

  document.addEventListener('tss-quiz:add_to_cart_full_routine', function (e) {
    push({
      event_name: 'add_all',
      cart_added: true,
      cart_items: (e.detail.variant_ids || []).map(function (v) { return { variant_id: v }; })
    });
    ga4(GA4_EVENTS.add_all, {
      item_count: e.detail.item_count,
      session_id: e.detail.session_id,
      persona_id: e.detail.persona_id
    });
    clabsPush('cl_quiz_add_to_cart_all', {
      quiz_data: {
        session_id: e.detail.session_id,
        persona_id: e.detail.persona_id,
        item_count: e.detail.item_count || 0,
        variant_ids: e.detail.variant_ids || []
      }
    });
  });

  // ---- Expose API for debugging -------------------------------------------
  window.TSSQuizAnalytics = {
    flush: push,
    context: captureContext,
    config: { url: SUPABASE_URL, table: SUPABASE_TABLE }
  };
})();
